import React, { useState, useEffect, useRef, useMemo } from 'react';
import { withRouter } from 'react-router-dom';
import Modal from 'react-bootstrap/Modal';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import ImageDrop from './ImageDrop';
import Filters from './Filters';
import groupResults from './groupResults';
import LocationCard from './LocationCard';
import useIsPhone from './useIsPhone';
import * as historyStore from './historyStore';
import TrailStrip from './TrailStrip';
import RecentPanel from './RecentPanel';
import SearchBar, { detectPlace } from './SearchBar';
import axios from 'axios';
import allowance from './allowance';

function App(props) {
    const [image, setImage] = useState(null);
    const [results, setResults] = useState([]);
    const [loading, setLoading] = useState(false);
    const [shareBtnEnabled, setShareBtnEnabled] = useState(true);
    const [show404Error, setShow404Error] = useState(false);
    const [showUrlCopied, setShowUrlCopied] = useState(false);
    const EMPTY_FILTERS = { category_ids: [], permit_ids: [], city: '', county: '', near: '', radius: '' };
    const [filters, setFilters] = useState(EMPTY_FILTERS);
    // Shown as a dismissible banner. Never alert() — see the banner's comment.
    const [errorMessage, setErrorMessage] = useState(null);
    // Badge threshold, supplied by the server with each result set so the client
    // holds no copy that can drift when it is retuned.
    const [strongDistance, setStrongDistance] = useState(null);
    // Location excluded from the current search, set by Find similar. Held in
    // state so a filter change afterwards keeps excluding it.
    const [excludeLocationId, setExcludeLocationId] = useState(null);
    // Token whose fetch should be skipped once — set immediately before the
    // push that triggers it (see shareUrlClicked), never earlier: arming on
    // click lets an unrelated navigation during the request consume the skip.
    const skipTokenRef = useRef(null);
    // Same pattern for a self-initiated ?hist push (upload recording): the
    // results are already on screen, so the navigation effect must not replay.
    const skipHistRef = useRef(null);
    // Armed by Find similar before its push: tells the navigation effect this
    // src navigation is a NEW search (run it live, with this exclusion) rather
    // than a revisit to replay. Without it, find-similar on a previously
    // searched image would replay the old snapshot and skip re-parenting.
    const liveSearchRef = useRef(null);
    // Shown when a ?hist URL points at an entry this browser no longer has
    // (evicted, cleared, or someone else's pasted link) — expiry should read
    // as expiry, not as the feature breaking.
    const [histNotice, setHistNotice] = useState(false);
    // KTD8: a replayed upload has no live-search path (the File is gone), so
    // the filter bar locks rather than rendering silently dead controls.
    const [filtersLocked, setFiltersLocked] = useState(false);
    // Armed by a Recent-panel pick: that replay is a re-entry and lands at
    // the top of the page, where Back/trail replays resume mid-scroll.
    const scrollTopNextReplayRef = useRef(false);
    // The text search on screen, or null. State for render (toolbar swaps the
    // reference thumbnail for the editable bar), ref for effects (the
    // debounced filter timer reads it at fire time, not at schedule time).
    const [activeQuery, setActiveQuery] = useState(null);
    const activeQueryRef = useRef(null);
    const setActiveQ = (q) => {
        activeQueryRef.current = q;
        setActiveQuery(q);
    };
    // Bumped whenever the user abandons or changes what they're looking at.
    // Every request captures it and drops its response if it no longer matches,
    // so a slow response can't overwrite newer state (or resurrect cleared state).
    const requestGen = useRef(0);
    // The ?src= URL the current results came from, or null. `image` can't be
    // used for this: a share token also stores a string there, and a share
    // replays stored results rather than re-querying.
    const searchSrcRef = useRef(null);
    // The signature that authorises searchSrcRef's src (server-minted; the
    // API 403s an unsigned src). Kept beside the src — never part of any
    // identity check (KTD3: sig is transport) — so a filter re-run of the
    // same src can re-send it. Null for pre-signing links; those live
    // re-runs degrade to the server's 403 message (R4).
    const searchSigRef = useRef(null);
    // The history entry the on-screen results belong to. State, not a ref:
    // the trail derives from it and must re-render when it changes.
    const [currentEntryId, setCurrentEntryId] = useState(null);
    // Armed by Find similar with the entry being LEFT, consumed by the next
    // record — that hop is what links a child entry to its parent chain.
    // Everything else leaves it null, which record() treats as "preserve the
    // entry's existing chain / create parentless".
    const pendingParentRef = useRef(null);
    // The live upload's entry. Upload entries have no src to dedupe on, so a
    // filter re-search of the same upload must update by id — without this,
    // every filter tick would mint a duplicate history entry.
    const uploadEntryIdRef = useRef(null);

    const reportRequestError = (error, fallback) => {
        console.log(error);
        const data = error.response && error.response.data;
        setErrorMessage((data && (data.message || data.error)) || fallback);
    };

    // Paid search calls go through here: await the in-flight allowance
    // mint (it resolves null at the script ceiling — the mount-fired
    // Find Similar search must not race it), attach the header or the
    // script-blocked marker, and retry exactly once on the allowance
    // refusal codes (never on 429/5xx — see allowance.shouldRetry).
    const searchWithAllowance = async (send) => {
        const headers = await allowance.headers(false);

        try {
            return await send(headers);
        } catch (error) {
            if (!allowance.shouldRetry(error)) throw error;

            const fresh = await allowance.headers(true);

            if (!fresh['X-Search-Allowance']) throw error;

            return send(fresh);
        }
    };

    // Restore a search from its history snapshot — no network. Restores the
    // FULL state set: results/image/filters, plus searchSrcRef (filters
    // no-op without it), the entry's find-similar exclusion (a refine after
    // replay would otherwise return the clicked photo as its own top match),
    // and the Share button (left disabled after viewing a share token).
    const replayEntry = (entry) => {
        // Invalidate anything in flight so a slow response can't overwrite
        // the replayed state.
        ++requestGen.current;
        // Any replay abandons a hop that may still be armed — leaving the
        // parent ref set would re-parent an unrelated entry on its next
        // record (a false trail, or even a stored cycle).
        pendingParentRef.current = null;
        const f = entry.filters || EMPTY_FILTERS;
        // Seed BEFORE setFilters, or the filter effect reads the change as
        // user input and fires a search at the server.
        lastFilterSignature.current = JSON.stringify(filterEntries(f));
        setFilters(f);
        setLoading(false);
        setErrorMessage(null);
        setHistNotice(false);
        // Uploads keep only their thumbnail — it becomes the reference image
        // and, on share, the posted file (KTD5).
        setImage(entry.src || entry.thumb || null);
        setActiveQ(entry.q || null);
        setResults(entry.results || []);
        setStrongDistance(entry.strongDistance ?? null);
        setExcludeLocationId(entry.excludeLocationId || null);
        setShareBtnEnabled(true);
        searchSrcRef.current = entry.src || null;
        searchSigRef.current = entry.sig || null;
        // A replayed upload cannot re-search (File gone) — lock filters
        // rather than leaving dead controls. Text replays CAN re-search
        // (the URL re-runs them), so they stay unlocked.
        setFiltersLocked(!entry.src && !entry.q);
        uploadEntryIdRef.current = null;
        setCurrentEntryId(entry.id);
        // Put the user back where they were when they left this search via
        // Find similar; entries never left that way land at the top. A
        // Recent-panel pick arms the override — a re-entry starts at the top.
        const toTop = scrollTopNextReplayRef.current;
        scrollTopNextReplayRef.current = false;
        requestAnimationFrame(() => window.scrollTo(0, toTop ? 0 : (entry.scrollY || 0)));
    };

    // Start the allowance exchange at app mount, in parallel with URL
    // parsing, so the mount-fired deep-link search waits only on residual
    // mint latency (never a cold start when the user hits Search).
    useEffect(() => {
        allowance.prime();
    }, []);

    // THE navigation spine: every search-state transition is driven by the
    // URL, so Back/Forward, trail clicks, Recent clicks, and pushes all take
    // the same path. Resolution order: share token → replay-or-live by
    // ?hist/?src → clean page. This effect used to key on the token alone —
    // which is exactly why Back changed the URL while the results stayed put.
    useEffect(() => {
        const queryParams = new URLSearchParams(props.location.search);
        const src = queryParams.get('src');
        // The src's server-minted signature, carried beside it in every URL
        // our own surfaces emit. Read here so replays and live runs share it.
        const sig = queryParams.get('sig');
        const hist = queryParams.get('hist');
        const token = props.match.params.token;

        if (token) {
            searchSrcRef.current = null;
            searchSigRef.current = null;
            // Cleared at kickoff, not on success: the debounced filter effect
            // reads activeQueryRef synchronously, and a stale text query left
            // over from the previous search would win that branch and push
            // the OLD ?q= URL — navigating away from these results.
            setActiveQ(null);
            setFiltersLocked(false);
            if (skipTokenRef.current === token) {
                // We just created this share; its results are already in state.
                skipTokenRef.current = null;
                return;
            }
            const gen = ++requestGen.current;
            setLoading(true);
            axios.get(`/api/get-shared-search/${token}`)
                .then((response) => {
                    if (gen !== requestGen.current) return;
                    setLoading(false);
                    if (response.data) {
                        setShareBtnEnabled(false);
                        setImage(response.data.image_url);
                        setResults(response.data.results);
                    }
                })
                .catch((error) => {
                    if (gen !== requestGen.current) return;
                    setLoading(false);
                    if (error.response && error.response.status === 404) {
                        setShow404Error(true);
                        setTimeout(() => {
                            setShow404Error(false);
                            props.history.push("/");
                        }, 1500);
                    } else {
                        console.log(error);
                    }
                });
            return;
        }

        const q = queryParams.get('q');
        if (q) {
            searchSrcRef.current = null;
            searchSigRef.current = null;
            setFiltersLocked(false);
            const urlFilters = filtersFromParams(queryParams);
            const entry = historyStore.findByQuery(q, urlFilters);
            if (entry) {
                replayEntry(entry);
            } else {
                // Fresh arrival or a filter change that minted a new
                // q+filters combination: seed the signature BEFORE setting
                // filter state so the filter effect doesn't fire a duplicate,
                // then search live.
                scrollTopNextReplayRef.current = false;
                lastFilterSignature.current = JSON.stringify(filterEntries(urlFilters));
                // The URL can't carry a half-entered distance filter — both
                // filterEntries and entryHref gate on near AND radius — so a
                // wholesale overwrite would erase what the user is mid-way
                // through typing. Carry the partial pair over; it's invisible
                // to the search and the signature either way.
                setFilters((prev) => {
                    const partial = (prev.near || prev.radius) && !(prev.near && prev.radius);
                    return partial && !urlFilters.near && !urlFilters.radius
                        ? { ...urlFilters, near: prev.near, radius: prev.radius }
                        : urlFilters;
                });
                setExcludeLocationId(null);
                runTextSearch(q, urlFilters, ++requestGen.current);
            }
            return;
        }

        if (hist) {
            if (skipHistRef.current === hist) {
                // Our own push after recording an upload — results are
                // already on screen.
                skipHistRef.current = null;
                return;
            }
            const entry = historyStore.findById(hist);
            if (entry) {
                replayEntry(entry);
            } else {
                // Evicted, cleared, or another browser's link: land clean,
                // but say why rather than silently eating the navigation.
                scrollTopNextReplayRef.current = false;
                clearSearch();
                setHistNotice(true);
            }
            return;
        }

        if (src) {
            const live = liveSearchRef.current;
            liveSearchRef.current = null;
            searchSrcRef.current = src;
            searchSigRef.current = sig || null;
            // Same kickoff-time clear as the token branch — success handlers
            // are too late for the filter effect's synchronous ref read, and
            // a find-similar hop out of a text search takes the live path
            // below without ever reaching the replay lookup.
            setActiveQ(null);
            setFiltersLocked(false);
            if (live && live.src === src) {
                // A Find similar hop: a NEW search, never a replay — the
                // record on success also re-parents the chain.
                runUrlSearch(src, ++requestGen.current, live.exclude, live.sig || sig || null);
                return;
            }
            // Lookup by src ALONE — the sig is transport, never identity
            // (KTD3): every valid sig for a src is the same string, and a
            // sigless pre-signing entry is still the same search.
            const entry = historyStore.findBySrc(src);
            if (entry) {
                // Heal stored entries from the URL: a signed arrival carries
                // the current sig, so persist it — future filter re-runs and
                // Recent/trail links then carry it too. DIFFERENT, not merely
                // missing: an upgrade-only heal can never repair a stored sig
                // that stopped verifying (APP_KEY rotation, a hand-edited
                // link), and replayEntry would keep loading the stale one until
                // the next filter change 403s.
                if (sig && entry.sig !== sig) {
                    entry.sig = sig;
                    historyStore.patch(entry.id, { sig });
                }
                replayEntry(entry);
            } else {
                // Fresh arrival (pasted URL, evicted entry): search live —
                // with NO exclusion. State may still hold the previous
                // search's excludeLocationId, which would silently drop a
                // location from these unrelated results.
                scrollTopNextReplayRef.current = false;
                setExcludeLocationId(null);
                runUrlSearch(src, ++requestGen.current, null, sig || null);
            }
            return;
        }

        clearSearch();
    }, [props.location.search, props.match.params.token]);

    useEffect(() => {
        if (image && typeof image !== 'string') {
            onSearchSubmit();
        }
    }, [image]);

    const handleImageDrop = (file) => {
        // A new upload is a new search, not a re-record of the previous one —
        // and not a continuation of whatever came before it: clear the src so
        // a filter tick can't re-run the ABANDONED url search (the src branch
        // outranks the upload branch in the filter effect), and unlock the
        // filters a replayed upload may have locked.
        uploadEntryIdRef.current = null;
        searchSrcRef.current = null;
        searchSigRef.current = null;
        setActiveQ(null);
        setFiltersLocked(false);
        setImage(file);
    };

    // Filters re-run the search server-side rather than trimming what we
    // already have: the filter belongs INSIDE the vector search, or a narrow
    // filter returns an empty page while matching images sit unqueried.
    const isFirstFilterRender = useRef(true);
    // Last filter set actually sent, so an edit that changes nothing the server
    // sees does not cost a request (or, on the upload path, a re-upload).
    const lastFilterSignature = useRef(null);

    useEffect(() => {
        if (isFirstFilterRender.current) {
            isFirstFilterRender.current = false;
            // Seed with the mount-time filter set. Left at null, the first
            // change never matches, so one request always slipped through
            // before the guard could take effect. Seed ONLY when still null:
            // a cold load straight onto a stored ?src entry replays during
            // the same mount and seeds the SNAPSHOT's signature first —
            // overwriting it here (closure still holds EMPTY_FILTERS) made
            // every reload of a filtered replay fire a spurious live search
            // 600ms later.
            if (lastFilterSignature.current === null) {
                lastFilterSignature.current = JSON.stringify(filterEntries());
            }
            return;
        }

        // Nothing the SERVER can see has changed — e.g. typing a place with no
        // radius selected, which buildFilter ignores until both are set. On the
        // upload path a re-search re-posts the original file (that endpoint has
        // no cache), so a no-op here is expensive, not just wasteful.
        const signature = JSON.stringify(filterEntries());
        if (signature === lastFilterSignature.current) return;

        // Debounced: ticking three boxes should cost ONE search, not three.
        // Each change cancels the pending one, so the request fires only once
        // the user pauses — which also keeps this well inside the 60/hr limit.
        const timer = setTimeout(() => {
            // Arrived via ?src= (searching from a location gallery): `image`
            // holds the URL string, not a File. This is the common entry
            // point, and it used to be skipped entirely because the only
            // guard here recognised an uploaded File — which is why filters
            // appeared to do nothing.
            lastFilterSignature.current = signature;

            if (activeQueryRef.current) {
                // KTD4's invariant: on a text search the address bar always
                // equals the current q+filters — the URL is the share link,
                // so Share must never copy a stale one. Push the updated URL
                // and let the navigation effect resolve replay-or-live.
                props.history.push(historyStore.entryHref({ q: activeQueryRef.current, filters }));
                return;
            }

            if (searchSrcRef.current) {
                // Bumped here rather than at schedule time: a response is
                // invalidated only once a newer request actually starts.
                runUrlSearch(searchSrcRef.current, ++requestGen.current);
                return;
            }
            // An uploaded File. A share token also leaves a string in `image`,
            // and deliberately falls through both branches: a share replays
            // its stored results rather than re-querying.
            if (image && typeof image !== 'string') {
                onSearchSubmit();
            }
        }, 600);

        return () => clearTimeout(timer);
    }, [filters]);

    // Both search paths send the same filters; they differ only in transport
    // (multipart upload vs query string), so the set of keys lives in one place.
    // Takes the filter set as an argument (defaulting to state) so a history
    // replay can seed the signature guard from a SNAPSHOT's filters before the
    // corresponding state has applied.
    const filterEntries = (f = filters) => {
        const out = [];
        (f.category_ids || []).forEach((id) => out.push(["category_ids[]", id]));
        (f.permit_ids || []).forEach((id) => out.push(["permit_ids[]", id]));
        ['city', 'county'].forEach((key) => {
            if (f[key]) out.push([key, f[key]]);
        });
        // Both or neither: buildFilter ignores a place until a radius is set
        // too (`$near !== '' && $radius > 0`). Emitting `near` alone made this
        // disagree with the server, which broke the signature guard below — it
        // saw a change on every keystroke for a request that was identical.
        if (f.near && f.radius) {
            out.push(["near", f.near]);
            out.push(["radius", f.radius]);
        }
        return out;
    };

    const appendFilters = (formData) => {
        filterEntries().forEach(([key, value]) => formData.append(key, value));
        return formData;
    };

    // The inverse of entryHref's text-URL shape: a pasted ?q= link must
    // reconstruct the filter state it describes (AE3).
    const filtersFromParams = (queryParams) => ({
        category_ids: queryParams.getAll('category_ids[]').map(Number).filter((n) => n > 0),
        permit_ids: queryParams.getAll('permit_ids[]').map(Number).filter((n) => n > 0),
        city: queryParams.get('city') || '',
        county: queryParams.get('county') || '',
        near: queryParams.get('near') || '',
        radius: queryParams.get('radius') || '',
    });

    const runTextSearch = (q, f, gen) => {
        setLoading(true);
        setErrorMessage(null);

        const entries = [["q", q]].concat(filterEntries(f));

        searchWithAllowance((headers) =>
            axios.get(`/api/search-from-text?${new URLSearchParams(entries).toString()}`, { headers })
        )
            .then(response => {
                if (gen !== requestGen.current) return;
                setLoading(false);
                if (response.data && response.data.answer_records) {
                    // No reference image for a text search — the toolbar
                    // renders the editable bar in that slot instead.
                    setImage(null);
                    setActiveQ(q);
                    setResults(response.data.answer_records);
                    setStrongDistance(response.data.strong_distance ?? null);

                    const parent = pendingParentRef.current;
                    pendingParentRef.current = null;
                    const entry = historyStore.record({
                        q,
                        filters: f,
                        results: response.data.answer_records,
                        strongDistance: response.data.strong_distance ?? null,
                        excludeLocationId: null,
                        parentId: parent || undefined,
                    });
                    setCurrentEntryId(entry ? entry.id : null);
                }
            })
            .catch(error => {
                if (gen !== requestGen.current) return;
                setLoading(false);
                pendingParentRef.current = null;
                reportRequestError(error, "Something went wrong with the search — please try again.");
            });
    };

    const runUrlSearch = (src, gen, excludeId, sig) => {
        setLoading(true);
        setErrorMessage(null);

        // Passed in rather than read from state: setExcludeLocationId on the
        // same tick has not applied yet, so state would send the PREVIOUS
        // exclusion (null on the first refine). `undefined` means "use state",
        // which is what the debounced filter effect wants.
        const exclude = excludeId === undefined ? excludeLocationId : excludeId;
        // Same convention for the signature: `undefined` means "use the ref"
        // (the filter effect re-runs the current src and must keep sending
        // its sig), an explicit value wins. Absent entirely — a pre-signing
        // link — the param is omitted and the server's 403 message shows.
        const sigVal = sig === undefined ? searchSigRef.current : sig;
        const entries = [["src", src]].concat(filterEntries());
        if (sigVal) entries.push(["sig", sigVal]);
        if (exclude) entries.push(["exclude_location_id", exclude]);

        const params = new URLSearchParams(entries);

        searchWithAllowance((headers) =>
            axios.get(`/api/search-from-url?${params.toString()}`, { headers })
        )
            .then(response => {
                if (gen !== requestGen.current) return;
                setLoading(false);
                if (response.data && response.data.answer_records) {
                    setImage(src);
                    setActiveQ(null);
                    setResults(response.data.answer_records);
                    setStrongDistance(response.data.strong_distance ?? null);

                    // Success only — a failed search is not a place to return
                    // to. Zero-result responses ARE recorded: they are real
                    // searches the user may want to revisit and refine.
                    const parent = pendingParentRef.current;
                    pendingParentRef.current = null;
                    const entry = historyStore.record({
                        src,
                        // Persisted so replays' later live re-runs (filter
                        // change) and entryHref links stay authorised.
                        sig: sigVal || null,
                        filters,
                        results: response.data.answer_records,
                        strongDistance: response.data.strong_distance ?? null,
                        excludeLocationId: exclude || null,
                        // A string re-parents (find-similar hop); undefined
                        // preserves the chain (filter re-search of the same src).
                        parentId: parent || undefined,
                    });
                    setCurrentEntryId(entry ? entry.id : null);
                }
            })
            .catch(error => {
                if (gen !== requestGen.current) return;
                setLoading(false);
                // A failed hop must not leave its parent armed for whatever
                // search happens to succeed next.
                pendingParentRef.current = null;
                reportRequestError(error, "Something went wrong with the image search — please try again.");
            });
    };

    const onSearchSubmit = () => {
        const formData = new FormData();
        formData.append("file", image);
        appendFilters(formData);
        setLoading(true);
        setErrorMessage(null);
        // Bumped like every other search start: a new upload invalidates any
        // in-flight search, and two rapid uploads must not both land.
        const gen = ++requestGen.current;
        axios.post("/api/upload-image", formData, {
            headers: {
                "X-CSRF-TOKEN": csrf_token, // Ensure csrf_token is available
            },
        })
            .then((response) => {
                if (gen !== requestGen.current) return;
                setLoading(false);
                if (response.data && response.data.answer_records) {
                    setActiveQ(null);
                    setResults(response.data.answer_records);
                    setStrongDistance(response.data.strong_distance ?? null);

                    // The thumbnail is the only image kept for an upload — the
                    // File itself is deliberately not stored. Record via the
                    // live entry's id when this is a filter re-search of the
                    // same upload (no src to dedupe on), else create and give
                    // the search a Back/Forward slot via its ?hist URL.
                    const existingId = uploadEntryIdRef.current;
                    historyStore.makeThumbnail(image).then((thumb) => {
                        // The thumbnail is async (canvas decode — seconds for
                        // a huge photo on a slow device). If the user moved on
                        // meanwhile (New search, Find similar), recording or
                        // pushing now would hijack the URL of whatever they
                        // are looking at.
                        if (gen !== requestGen.current) return;
                        // No thumbnail (undecodable file) means no replayable
                        // entry: without a reference image the replay renders
                        // a blank page, and a Recent row that promises results
                        // it can't show is worse than no row.
                        if (!thumb && !existingId) return;
                        const entry = historyStore.record({
                            id: existingId || undefined,
                            src: null,
                            thumb,
                            filters,
                            results: response.data.answer_records,
                            strongDistance: response.data.strong_distance ?? null,
                            excludeLocationId: null,
                            parentId: existingId ? undefined : null,
                        });
                        setCurrentEntryId(entry ? entry.id : null);
                        if (entry) uploadEntryIdRef.current = entry.id;
                        if (entry && !existingId) {
                            // Give the upload a history slot. The navigation
                            // effect skips this self-initiated push — the
                            // results are already on screen.
                            skipHistRef.current = entry.id;
                            props.history.push('/?hist=' + entry.id);
                        }
                    });
                }
            })
            .catch((error) => {
                if (gen !== requestGen.current) return;
                setLoading(false);
                // Surface throttle/validation/unavailable errors instead of a
                // spinner that silently dies.
                reportRequestError(error, "Something went wrong with the image search — please try again.");
            });
    }

    // Grouped for display only — `results` stays the flat list the API
    // returned, which is what gets posted when sharing, so the share payload
    // keeps its shape and its size caps keep their meaning.
    const groups = useMemo(() => groupResults(results), [results]);

    // The match ladder (R2/R3): split the cards by their best image's tier.
    // Records arrive distance-sorted, so a group's first image (its cover) is
    // its best match and sets the group's tier. Records with NO _tier — text
    // searches, which the server never tiers — fall into the confident bucket,
    // which renders as today's single plain grid. Headings appear only above a
    // non-empty close/floor set, so the common all-confident page renders
    // byte-identical to the pre-ladder page. The floor is all-or-nothing by
    // construction (the server floors only when NOTHING made confident or
    // close), so a floor page is just the floor heading and its grid.
    const tierGroups = useMemo(() => {
        const confident = [];
        const close = [];
        const floor = [];
        groups.forEach((group) => {
            const tier = group.cover && group.cover._tier;
            if (tier === 'close') close.push(group);
            else if (tier === 'floor') floor.push(group);
            else confident.push(group);
        });
        return { confident, close, floor };
    }, [groups]);

    // Derived from the same list the request is built from, so the empty-state
    // copy can no longer disagree with what was actually filtered.
    const hasActiveFilters = useMemo(() => filterEntries().length > 0, [filters]);

    // Only the filter presentation (bottom sheets vs dropdowns) and the
    // toolbar's centred-vs-right layout are width-gated now — the results are
    // cards at every width.
    const isPhone = useIsPhone();

    // A new search replaces a tall list of results with a ~300px spinner, so the
    // document collapses and the browser clamps the scroll to whatever the new
    // maximum is. Measured on a phone: scrolled to 8000 in a 17437px page,
    // starting a search left the user at 3240 in a 4080px page — past the
    // spinner, and when that search returned no matches, past the "No close
    // matches found" message too. Nothing about the search appeared to happen.
    //
    // Go to the TOP of the page, not the results area: scrolling to a mid-page
    // anchor right as the document collapses produced two visible jumps in
    // quick succession (clamp, then anchor), which read as buggy — the exact
    // complaint that changed this from scrollIntoView(resultsArea). The top is
    // stable through the collapse, and it is where the reference image and
    // trail live, which is the orientation a new search needs.
    useEffect(() => {
        if (!loading) return;

        // Instant, not smooth. A smooth scroll is animated over several frames,
        // and the results landing part-way through is a second layout change
        // that cancels it — measured leaving the page at 3118 when the target
        // was 252. The jump also reads better here: the user asked for a new
        // search, so arriving at the top of it is the expected outcome rather
        // than a transition to watch.
        window.scrollTo(0, 0);
    }, [loading]);

    const clearSearch = () => {
        // Invalidate anything in flight so a late response can't resurrect
        // what the user just cleared.
        requestGen.current++;
        skipTokenRef.current = null;
        skipHistRef.current = null;
        // Otherwise a filter change after "New Search" would re-run the search
        // the user just cleared.
        searchSrcRef.current = null;
        searchSigRef.current = null;
        // The history ENTRIES survive New Search (they are the point of
        // history); only the live-view linkage resets.
        uploadEntryIdRef.current = null;
        pendingParentRef.current = null;
        setCurrentEntryId(null);
        setActiveQ(null);
        setFiltersLocked(false);
        setHistNotice(false);
        setExcludeLocationId(null);
        setErrorMessage(null);
        setImage(null);
        setResults([]);
        setFilters(EMPTY_FILTERS);
        setShareBtnEnabled(true);
        setLoading(false);
        if (props.location.pathname !== "/") {
            props.history.push("/");
        }
    }

    const findSimilarClicked = (result) => {
        if (!result || !result._url) return;

        // The conversion, not the original: this URL becomes both the query
        // image Ximilar fetches and the toolbar's 64px reference thumbnail.
        // _url is the full-size asset (measured 3000px wide).
        const querySrc = result._thumb || result._url;
        // The record's server-minted signature signs exactly that value
        // (_thumb ?: _url, KTD2) — the frontend never chooses what to sign.
        const querySig = result._sig || null;

        // Exclude the clicked location from its own refine search, or Ximilar
        // hands back that same photo at ~0.003 as the new top result.
        setExcludeLocationId(result.location_id || null);
        searchSrcRef.current = querySrc;
        searchSigRef.current = querySig;

        // This hop is what builds the trail: the search being LEFT becomes
        // the parent of the one being started. Also remember where the user
        // was on the page, so Back can put them back at that card rather
        // than the top of the list.
        if (currentEntryId) {
            historyStore.patch(currentEntryId, { scrollY: window.scrollY });
        }
        pendingParentRef.current = currentEntryId;
        // A find-similar hop leaves any upload behind — the new search is
        // src-based, so a later filter change must not re-record the upload.
        uploadEntryIdRef.current = null;

        // Push-only: the navigation effect runs the search. The armed ref
        // tells it this is a NEW search to run live (with this exclusion),
        // not a revisit to replay from snapshot.
        //
        // Except when the target URL IS the current URL (Find similar on the
        // search's own source image): pushing an identical search string does
        // not change `location.search`, so the effect would never fire and
        // the armed ref would lie in wait to misfire on a later navigation.
        // Run that one directly.
        const sameSrc = new URLSearchParams(props.location.search).get('src') === querySrc
            && !props.match.params.token;
        if (sameSrc) {
            runUrlSearch(querySrc, ++requestGen.current, result.location_id || null, querySig);
            return;
        }
        liveSearchRef.current = { src: querySrc, exclude: result.location_id || null, sig: querySig };
        props.history.push('/?src=' + encodeURIComponent(querySrc)
            + (querySig ? '&sig=' + encodeURIComponent(querySig) : ''));

        // No explicit scroll here — the search sets `loading`, and the
        // effect above scrolls on every search rather than only this one.
    };

    // The text way in: detect a place word against the facet lists, fold it
    // into the filters, and push the URL — the navigation effect runs the
    // search (same push-only discipline as find-similar). Re-detection runs
    // on every submit, including toolbar edits (KTD7).
    const onTextSubmit = (phrase) => {
        const facets = props.facets || {};
        const detected = detectPlace(phrase, facets);
        const nextFilters = {
            ...filters,
            city: detected.city || (detected.county ? '' : filters.city),
            county: detected.county || (detected.city ? '' : filters.county),
        };
        // A text search leaves any upload/src context behind.
        uploadEntryIdRef.current = null;
        searchSrcRef.current = null;
        searchSigRef.current = null;
        if (currentEntryId) {
            historyStore.patch(currentEntryId, { scrollY: window.scrollY });
        }
        const href = historyStore.entryHref({ q: detected.q, filters: nextFilters });
        if (href === '/' + props.location.search) {
            // Identical URL: the effect won't re-fire. Usually nothing to do —
            // but after a FAILED search the URL already matches, so a no-op
            // here would swallow the retry the error message just asked for.
            // Run that one directly.
            if (errorMessage) {
                lastFilterSignature.current = JSON.stringify(filterEntries(nextFilters));
                setFilters(nextFilters);
                setExcludeLocationId(null);
                runTextSearch(detected.q, nextFilters, ++requestGen.current);
            }
            return;
        }
        // Seed BEFORE setFilters so the filter effect doesn't fire its own
        // search on top of the navigation's.
        lastFilterSignature.current = JSON.stringify(filterEntries(nextFilters));
        setFilters(nextFilters);
        props.history.push(href);
    };

    const shareUrlClicked = () => {
        // A text search's URL IS its share link — the address fully describes
        // the search (KTD6). No token, no server storage.
        if (activeQueryRef.current) {
            navigator.clipboard.writeText(window.location.href).then(() => {
                onClipboardCopy();
            });
            return;
        }
        setShareBtnEnabled(false);

        const gen = requestGen.current;
        const formData = new FormData();
        // After a replayed upload, `image` holds the stored thumbnail data
        // URL — the original File is deliberately gone. The share endpoint
        // accepts uploads and same-host URLs, not data URLs, so rebuild a
        // file from the thumbnail: the share then carries the full result
        // snapshot with the downscaled reference image (R8's accepted trade).
        let filePart = image;
        if (typeof image === 'string' && image.startsWith('data:')) {
            filePart = historyStore.dataUrlToFile(image, 'search-reference.jpg');
        }
        formData.append("file", filePart);
        formData.append("results", JSON.stringify(results));

        axios.post("/api/share-search-url", formData, {
            headers: {
                "X-CSRF-TOKEN": csrf_token,
            },
        })
            .then((response) => {
                // Abandoned (New Search / navigated away) while in flight.
                if (gen !== requestGen.current) return;

                if (response.data && response.data.token) {
                    const token = response.data.token;
                    // Arm the skip immediately before the push that triggers
                    // the effect — never earlier.
                    skipTokenRef.current = token;
                    props.history.push(`/${token}`);
                    onClipboardCopy();
                }
            })
            .catch((error) => {
                if (gen !== requestGen.current) return;
                // Failed share: re-enable the button so the UI isn't stuck.
                setShareBtnEnabled(true);
                reportRequestError(error, "Couldn't create the share link — please try again.");
            });
    }

    const closeModal = () => {
        setShow404Error(false);
    }

    const onClipboardCopy = () => {
        setShowUrlCopied(true);
        setTimeout(() => {
            setShowUrlCopied(false);
        }, 500);
    }

    const copyImageLink = (e, slug, mediaId) => {
        e.preventDefault();
        e.stopPropagation();
        let url = window.location.origin + '/' + slug + '?img=' + mediaId;
        navigator.clipboard.writeText(url).then(() => {
            onClipboardCopy();
        });
    };

    const sourceMediaId = useMemo(() => {
        const src = new URLSearchParams(props.location.search).get('src') || '';
        // Compare on media id, not URL: `src` is usually a conversion
        // (…/conversions/x-medium.jpg) while the record's _url is the original,
        // so string equality never matches even for the same photo.
        const match = /\/media\/(\d+)\//.exec(src);
        return match ? match[1] : null;
    }, [props.location.search]);

    // Memoized so a blob URL is minted once per File (not once per render) —
    // and revoked when it's replaced, instead of leaking for the tab's life.
    const imgUrl = useMemo(
        () => (!image ? "" : (typeof image === 'string' ? image : URL.createObjectURL(image))),
        [image]
    );

    useEffect(() => {
        if (imgUrl && typeof image !== 'string') {
            return () => URL.revokeObjectURL(imgUrl);
        }
    }, [imgUrl]);

    // One tier's card grid. The map body is shared by every tier on purpose:
    // confident cards must render exactly as they always have, and close/floor
    // cards differ only in which list they arrive in — their bestDistance is
    // above strongDistance by definition (strong ≤ max < close), so the badge
    // logic needs no special-casing to stay silent for them.
    const renderCardGrid = (tierList) => (
        /* One card per location at every width — columns are CSS (see
           .il-image-search-cards). Extra images live in a scroller inside the
           card; there is no expand/collapse anywhere any more. */
        <div className={"il-image-search-cards" + (loading ? " il-searching" : "")}>
            {tierList.map((group) => {
                // A gallery search returns its own source location
                // first, at ~0.003 — near-identical because the query
                // image IS in the collection. Badging that "Strong
                // match" for being itself is noise, so it is excluded.
                const isSource = !!sourceMediaId
                    && group.images.some((i) => String(i._id) === sourceMediaId);
                const strongMatch = !isSource
                    && strongDistance != null
                    && typeof group.bestDistance === 'number'
                    && group.bestDistance <= strongDistance;

                return (
                    <LocationCard
                        key={group.key}
                        group={group}
                        onCopyLink={copyImageLink}
                        onFindSimilar={findSimilarClicked}
                        strongMatch={strongMatch}
                    />
                );
            })}
        </div>
    );

    let shareUrl = props.match.params.token ?
        window.location.protocol + "//" + window.location.hostname + "/ai-search/" + props.match.params.token :
        null;

    return (
        <>
            <div className="container-fluid">
                <div className="d-flex flex-column" style={{ minHeight: "calc(100vh - 400px)" }}>
                    <div className="mb-2 w-100 text-center">
                        {/* Sizing moved out of an inline style so it can respond
                            to the viewport — an inline max-width cannot be
                            overridden by a media query without !important. The
                            margins are Bootstrap utilities (also !important), so
                            they are swapped rather than overridden. */}
                        <div><svg xmlns="http://www.w3.org/2000/svg" className={"il-image-ai-logo " + (isPhone ? "mt-3 mb-2" : "mt-5 mb-3")} viewBox="0 0 222.6 29.19"><path d="M0 28.4h9V.81H0V28.4Zm112.84-15.38h16.93V28.4h-4.55l-1.21-3.21c-3.17 2.82-8.19 4-14 4-6.44 0-11.57-1.24-15-3.94l1.68 3.17h-9.72l-2.7-5.68h-15.2l-2.69 5.68H50.05V6.3h-.09L39.48 28.4h-7.57L21.4 6.3h-.09v22.1h-8.75V.81h13.26l9.86 19.83L45.54.78h13.25v23.58L71.26.81h10.88l8.41 15.83c-.09-.69-.13-1.38-.13-2.07C90.42 6.03 97.49 0 110.05 0c9.21 0 17.91 2.24 19.54 9.81h-10.05c-1.35-3.55-5.21-4.56-8.46-4.56-8.19 0-11.07 5-11.07 9.32s2.88 9.31 11.07 9.31c4.93 0 8.74-1.55 9.81-6.11h-8l-.05-4.75ZM81.91 17.7 76.7 6.76 71.48 17.7h10.43Zm60-.89h20.88v-5h-20.83V6.07h22V.81h-31V28.4h31.21v-5.26h-22.21l-.05-6.33Z"/><path d="m179.17 17.32-4.71 10.88h-.84L185.61.73h.73l11.88 27.47h-.88l-4.71-10.88h-13.45Zm13.14-.72c-3.98-9.31-5.9-13.56-6.32-14.9h-.04c-.31.92-3.18 7.39-6.44 14.9h12.8ZM205.57.73V28.2h-.8V.73h.8Zm9.81.19v.93h-1.8v4.71h-1.12V1.85h-1.8V.92h4.73Zm6.1 5.64-.21-3.32c-.02-.44-.02-.97-.04-1.58h-.06c-.15.5-.31 1.16-.48 1.68l-1.02 3.13h-1.16l-1.02-3.21c-.1-.44-.27-1.1-.39-1.6h-.06c0 .52-.02 1.06-.04 1.58l-.21 3.32h-1.08l.42-5.64h1.68l.97 2.76c.12.44.23.85.37 1.43h.02c.15-.52.27-1 .39-1.41l.97-2.78h1.62l.44 5.64h-1.12Z" /></svg></div>
                        {!image && (
                            <h1 className="mb-4 h5">Image Search - Provide an image to use as a reference for your production location search.</h1>
                        )}
                        {!!image && !loading && (
                            <>
                                {!shareBtnEnabled && (
                                    <div className="row">
                                        <div className="col-md-6 offset-md-3">
                                            {shareUrl ?
                                                <CopyToClipboard text={shareUrl}
                                                    onCopy={onClipboardCopy}>
                                                    <div className="w-100 bg-light pb-2 pt-3 px-5 mt-1 mb-3 mt-3 d-inline-block border btn position-relative share-btn">
                                                        <span style={{ right: '10px', top: '5px', fontSize: '12px' }} className="position-absolute">Share this URL</span>
                                                        <p className="font-weight-bold">{shareUrl}</p>
                                                    </div>
                                                </CopyToClipboard>
                                                :
                                                <div className="w-100 pb-2 pt-3 px-5 mt-1 mb-3 mt-3 d-inline-block">
                                                    <div className="d-flex align-items-center justify-content-center w-100">
                                                        <div className="spinner-grow text-primary" role="status"> <span className="sr-only">Loading...</span></div>
                                                    </div>
                                                </div>
                                            }


                                        </div>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                    <div className="row">
                        {/* `&& !imgUrl` restricts this to the FIRST search, when there is
                            nothing on screen to keep. Unconditionally, it replaced the whole
                            content area — filter bar included — on every re-search, so each
                            search remounted the dropdowns and reset them to closed. The
                            results-area spinner below covers the re-search case. */}
                        {loading && !imgUrl && !activeQuery ? (
                            <div className="d-flex align-items-center justify-content-center w-100" style={{ minHeight: '300px' }}>
                                <div className="spinner-grow text-primary" role="status"> <span className="sr-only">Loading...</span></div>
                            </div>
                        )
                            : (<>
                                {errorMessage && (
                                    /* Never alert(): a blocking dialog makes the whole tab
                                       unreachable, and this fires exactly when the user needs
                                       to adjust something and retry. Rendered here, outside the
                                       `imgUrl` branch, so upload-validation errors — which happen
                                       before any image exists — are visible too. */
                                    <div className="alert alert-warning alert-dismissible fade show w-100" role="alert">
                                        {errorMessage}
                                        <button
                                            type="button"
                                            className="close"
                                            aria-label="Close"
                                            onClick={() => setErrorMessage(null)}>
                                            <span aria-hidden="true">&times;</span>
                                        </button>
                                    </div>
                                )}

                                {histNotice && (
                                    /* A ?hist link that outlived its entry (evicted, cleared,
                                       or pasted into another browser). Without this, Back to an
                                       expired upload search lands on an unexplained empty page
                                       and reads as the feature breaking. */
                                    <div className="alert alert-light border alert-dismissible fade show w-100" role="alert">
                                        That search is no longer in your history.
                                        <button
                                            type="button"
                                            className="close"
                                            aria-label="Close"
                                            onClick={() => setHistNotice(false)}>
                                            <span aria-hidden="true">&times;</span>
                                        </button>
                                    </div>
                                )}

                                {!imgUrl && !activeQuery && (
                                    /* The text way in, above the photo way in (the accepted
                                       search-bar-first layout): typing a phrase is the lower-
                                       friction start, and the drop zone stays one glance away. */
                                    <div className="w-100 mb-2">
                                        <div style={{ maxWidth: '800px', marginLeft: 'auto', marginRight: 'auto' }}>
                                            {/* Seeded from the URL, not "": after a FAILED
                                                text search activeQuery is still null but the
                                                address bar holds the phrase — losing it here
                                                would make "please try again" a lie. */}
                                            <SearchBar
                                                initial={new URLSearchParams(props.location.search).get('q') || ""}
                                                disabled={loading}
                                                onSubmit={onTextSubmit}
                                            />
                                            <p className="text-center text-muted small text-uppercase my-2" style={{ letterSpacing: '1px' }}>or</p>
                                        </div>
                                    </div>
                                )}

                                <ImageDrop
                                    imagePreview={!!image || !!activeQuery}
                                    handleImageDrop={handleImageDrop}
                                    onError={setErrorMessage}
                                />

                                {!imgUrl && !activeQuery && (
                                    /* Filters BEFORE a search. They cannot return anything on
                                       their own — this page needs an image — so they are framed
                                       as optional constraints on the search about to run, never
                                       as a form with a missing submit button. They also carry
                                       through: appendFilters() already runs on the upload path,
                                       and the vocabulary is inlined in data-facets, so this
                                       costs no extra request. */
                                    /* Two divs on purpose. The parent is a Bootstrap .row
                                       (flex-wrap), and ImageDrop is `flex: 1`, so a max-width
                                       on THIS element caps its flex-basis and lets the dropzone
                                       sit beside it instead of above. The outer div stays full
                                       width to force the wrap; the inner one carries the 800px
                                       constraint that lines up with ImageDrop's baseStyle. */
                                    <div className="w-100 mt-4">
                                        <div style={{ maxWidth: '800px', marginLeft: 'auto', marginRight: 'auto' }}>
                                            <p className="text-muted small mb-2">Optional — narrow your search</p>
                                            <Filters
                                                facets={props.facets || { categories: [], permits: [], cities: [], counties: [], without_coordinates: 0 }}
                                                filters={filters}
                                                onChange={setFilters}
                                                disabled={loading}
                                            />
                                        </div>
                                    </div>
                                )}

                                {(!!imgUrl || !!activeQuery) && (
                                    <div className="w-100">
                                        {/* One compact toolbar replaces the col-md-4 column, which
                                            held a thumbnail and a button above a tall run of white
                                            space while the grid was squeezed into the remaining
                                            width. Share moves here as a SECONDARY action: it was
                                            the largest, reddest element on the page, above the
                                            results it shares. */}
                                        <div
                                            className={"il-image-search-toolbar d-flex align-items-center flex-wrap mb-3 px-3"
                                                + (isPhone ? " justify-content-center text-center" : "")}
                                            style={{ gap: '12px' }}
                                        >
                                            {activeQuery ? (
                                                /* The editable bar IS the reference for a text
                                                   search (KTD7) — edit in place, resubmit, done.
                                                   Never a read-only label the user must retype. */
                                                <SearchBar
                                                    compact
                                                    initial={activeQuery}
                                                    disabled={loading}
                                                    onSubmit={onTextSubmit}
                                                />
                                            ) : (
                                                <>
                                                    <img
                                                        className="object-contain bg-light border"
                                                        src={imgUrl}
                                                        alt="Reference image for this search"
                                                        style={{ width: '64px', height: '48px', flex: '0 0 auto' }}
                                                    />
                                                    <span className="small text-muted">Reference image</span>
                                                </>
                                            )}

                                            {!loading && groups.length > 0 && (
                                                /* Describes what is ON THE PAGE. Never "of <total>":
                                                   results are capped, so the true number of matching
                                                   locations in the library is unknowable. */
                                                <span className="small text-dark">
                                                    {groups.length} closest {groups.length === 1 ? 'location' : 'locations'}
                                                </span>
                                            )}

                                            {/* ml-auto is omitted rather than overridden on phones:
                                                it is margin-left: auto !important, and on a narrow
                                                screen it pushed the pair off the right edge. */}
                                            <div
                                                className={"il-image-search-toolbar-actions d-flex align-items-center"
                                                    + (isPhone ? "" : " ml-auto")}
                                                style={{ gap: '8px' }}
                                            >
                                                <RecentPanel
                                                    isPhone={isPhone}
                                                    onNavigate={(href) => {
                                                        // Picking the search already on screen would push
                                                        // an identical URL: the effect never fires (search
                                                        // string unchanged), Back gains a dead entry, and
                                                        // the armed flag would misfire on a LATER replay.
                                                        if (href === '/' + props.location.search) {
                                                            window.scrollTo(0, 0);
                                                            return;
                                                        }
                                                        // A Recent pick is a re-entry, not a resumption:
                                                        // it lands at the top, not mid-scroll (KTD4).
                                                        scrollTopNextReplayRef.current = true;
                                                        props.history.push(href);
                                                    }}
                                                    onCleared={() => setCurrentEntryId(null)}
                                                />
                                                <button onClick={clearSearch} className="btn btn-sm btn-primary">New search</button>
                                                {!!shareBtnEnabled && (
                                                    <button
                                                        onClick={shareUrlClicked}
                                                        title="Create a shareable link to these results"
                                                        className="btn btn-sm btn-outline-secondary">
                                                        Share results
                                                    </button>
                                                )}
                                            </div>
                                        </div>

                                        {/* px-3 replaces the 15px padding the old .col-md-8
                                            provided. The results grid is a Bootstrap .row with
                                            -15px side margins; without a padded parent to absorb
                                            them the grid overflowed the viewport and clipped the
                                            first and last card in every row. */}
                                        <div className="w-100 px-3">
                                            {/* The chain the user is walking, above the filter bar so it
                                                reads as provenance for the results below. Renders nothing
                                                until there are ≥2 hops. */}
                                            <TrailStrip
                                                currentEntryId={currentEntryId}
                                                onNavigate={(href) => {
                                                    // A tab click lands at the top, every time — restoring
                                                    // the saved mid-page position here made some clicks
                                                    // teleport and others not, which read as random
                                                    // jumping. The saved position still serves browser
                                                    // Back/Forward, where restoring is the convention.
                                                    scrollTopNextReplayRef.current = true;
                                                    props.history.push(href);
                                                }}
                                            />

                                            {/* OUTSIDE the loading branch on purpose. While it lived inside,
                                                every search unmounted the filter bar and remounted it on
                                                completion, which reset each dropdown to closed — you could
                                                never tick a second box. */}
                                            <Filters
                                                facets={props.facets || { categories: [], permits: [], cities: [], counties: [], without_coordinates: 0 }}
                                                filters={filters}
                                                onChange={setFilters}
                                                disabled={loading}
                                                locked={filtersLocked}
                                            />

                                            {loading && groups.length === 0 ? (
                                                /* Nothing to keep on screen (first search from a clean
                                                   page): the spinner is all there is to show. */
                                                <div className="d-flex align-items-center justify-content-center" style={{ minHeight: '300px' }}>
                                                    <div className="spinner-grow text-primary" role="status">
                                                        <span className="sr-only">Loading...</span>
                                                    </div>
                                                </div>
                                            ) : (
                                                <>
                                                {loading && (
                                                    /* Stale-while-searching: the previous results stay
                                                       rendered (dimmed, inert — see .il-searching) instead
                                                       of collapsing to a spinner. The collapse was the
                                                       jank: the document shrank, the scroll clamped, and
                                                       the page jumped twice per Find similar. With the old
                                                       grid held in place the page height never moves; this
                                                       row is the only signal a search is running. */
                                                    <div className="il-searching-indicator d-flex align-items-center justify-content-center py-2">
                                                        <div className="spinner-border spinner-border-sm text-primary mr-2" role="status">
                                                            <span className="sr-only">Searching…</span>
                                                        </div>
                                                        <span className="small text-muted">Searching…</span>
                                                    </div>
                                                )}
                                                {groups.length === 0 && !loading ? (
                                                    /* A filter matching nothing, or a photo unlike anything we
                                                       stock, used to render blank space — indistinguishable
                                                       from a failed or throttled search. */
                                                    <div className="text-center py-5">
                                                        <p className="mb-2 font-weight-bold">No close matches found.</p>
                                                        <p className="text-muted small mb-0">
                                                            {hasActiveFilters
                                                                ? "Try removing a filter, or widening the distance."
                                                                : activeQuery
                                                                    ? "Try different words — nothing in our library looked like that."
                                                                    : "Try a different photo — this one didn’t look like anything in our library."}
                                                        </p>
                                                    </div>
                                                ) : (
                                                <>
                                                {/* The ladder, top tier first. Each tier is its own card
                                                   grid; the heading renders only when its tier has cards,
                                                   so the everyday all-confident page is exactly the one
                                                   grid it has always been, with no heading at all. The
                                                   labels take the same .il-searching dim as the grids —
                                                   they belong to the results they introduce. */}
                                                {tierGroups.confident.length > 0 && renderCardGrid(tierGroups.confident)}
                                                {tierGroups.close.length > 0 && (
                                                    <>
                                                        <h2 className={"il-results-tier-label" + (loading ? " il-searching" : "")}>
                                                            Close matches
                                                        </h2>
                                                        {renderCardGrid(tierGroups.close)}
                                                    </>
                                                )}
                                                {tierGroups.floor.length > 0 && (
                                                    <>
                                                        <h2 className={"il-results-tier-label" + (loading ? " il-searching" : "")}>
                                                            No strong matches — the closest we found
                                                        </h2>
                                                        {renderCardGrid(tierGroups.floor)}
                                                    </>
                                                )}

                                                </>
                                                )}
                                                </>                                            )}
                                        </div>
                                    </div>
                                )}
                            </>)}
                    </div>
                </div>
            </div>
            <Modal
                centered
                dialogClassName="justify-content-center"
                contentClassName="w-auto px-4 py-3"
                show={showUrlCopied}
                onHide={() => setShowUrlCopied(false)}>
                <div className="d-flex align-items-center justify-content-center"> <span style={{ width: '40px' }} className="text-primary mr-2">
                    <svg role="img" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512">
                        <path fill="currentColor" d="M413.505 91.951L133.49 371.966l-98.995-98.995c-4.686-4.686-12.284-4.686-16.971 0L6.211 284.284c-4.686 4.686-4.686 12.284 0 16.971l118.794 118.794c4.686 4.686 12.284 4.686 16.971 0l299.813-299.813c4.686-4.686 4.686-12.284 0-16.971l-11.314-11.314c-4.686-4.686-12.284-4.686-16.97 0z" className=""></path>
                    </svg> </span> <span className="h4 mb-0">URL Copied</span>
                </div>
            </Modal>
            <Modal backdrop centered show={show404Error} onHide={closeModal}>
                <div className="mb-0 p-5" role="alert">
                    <p className="text-center"><strong className="alert-heading">This shared search doesn't exist or has expired. Please submit a new search.</strong></p>
                </div>
            </Modal>
        </>
    );
}

export default withRouter(App);
